import React, { useMemo, useEffect } from 'react'
import { Box, Button, LinearProgress } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import EmailIcon from 'components/material-symbol/icons/Email'
import PhoneIcon from 'components/material-symbol/icons/Phone'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import Divider from '@material-ui/core/Divider'
import GroupEventInfo from '../../components/group-event-info'
import useStyles from '../../components/group-event-registration-form.styles'
import PriceDetails from '../../components/price-details'
import { IconButtonBackArrow } from 'components/atoms/icons'
import Error from 'components/error'
import StripePaymentElementsForm from '../stripe-payment-elements-form'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { isRegistrationFullError } from '../../utils/registration-full'
import useReviewAndPay from './use-review-and-pay'

const ReviewAndPay = ({
  groupEvent,
  handlePrev,
  handleNext,
  paymentRequired,
  shouldFetchClientSecret,
  setShouldFetchClientSecret,
  shouldShowWaiver,
  stripePaymentFormRef,
  marketingOptInChecked,
  onRegistrationFull,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    participants,
    phoneNumber,
    contactAttributes,
    clientSecret,
    loading,
    error,
    success,
    message,
    handleClick,
  } = useReviewAndPay({
    groupEvent,
    handleNext,
    paymentRequired,
    shouldFetchClientSecret,
    setShouldFetchClientSecret,
    shouldShowWaiver,
    stripePaymentFormRef,
  })

  const registrationFull =
    isRegistrationFullError(error) || isRegistrationFullError(message)

  useEffect(() => {
    if (registrationFull && onRegistrationFull) {
      onRegistrationFull()
    }
  }, [registrationFull, onRegistrationFull])

  const submitButtonText = useMemo(() => {
    if (!success) {
      return message || 'Unable to proceed'
    }
    return shouldShowWaiver ? 'Continue' : 'Complete Registration'
  }, [success, message, shouldShowWaiver])

  if (loading) {
    return <LinearProgress />
  }

  if (error && !registrationFull) {
    return <Error error={error} />
  }

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      justifyContent="space-between"
      py={6}
      position="relative"
    >
      <Box
        mr={isMobile ? 0 : 2}
        style={{
          width: isMobile ? 'unset' : '525px',
        }}
        className={classes.stepContentWrapper}
      >
        <Typography variant="h6" gutterBottom>
          Participants
        </Typography>
        <Typography variant="body1" gutterBottom>
          {participants
            .map(
              participant => `${participant.firstName} ${participant.lastName}`,
            )
            .join(', ')}
        </Typography>

        <Box mt={3} mb={2}>
          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          <Box display="flex" alignItems="center" mb={1}>
            <PersonOutlineIcon className={classes.icon} />
            <Typography variant="body1">
              {contactAttributes.firstName} {contactAttributes.lastName}
            </Typography>
          </Box>
          {contactAttributes.email && (
            <Box display="flex" alignItems="center" mb={1}>
              <EmailIcon className={classes.icon} />
              <Typography variant="body1">{contactAttributes.email}</Typography>
            </Box>
          )}
          {phoneNumber && (
            <Box display="flex" alignItems="center" mb={1}>
              <PhoneIcon className={classes.icon} />
              <Typography variant="body1">{phoneNumber}</Typography>
            </Box>
          )}
        </Box>

        <Divider className={classes.horizontalDivider} />
        <GroupEventInfo groupEvent={groupEvent} />
        <Divider className={classes.horizontalDivider} />
        <PriceDetails
          groupEvent={groupEvent}
          paymentRequired={paymentRequired}
        />
        <Box mt={4} display="flex" justifyContent="center">
          <Button onClick={handlePrev} startIcon={<IconButtonBackArrow />}>
            Back to Edit Information
          </Button>
        </Box>
      </Box>
      <Box
        style={{
          width: isMobile ? 'unset' : '480px',
          top: isMobile ? 'auto' : '48px',
        }}
        position={isMobile ? 'relative' : 'sticky'}
        className={classes.stepContentWrapper}
      >
        <StripePaymentElementsForm
          ref={stripePaymentFormRef}
          clientSecret={clientSecret}
          secretLoading={loading}
          groupEvent={groupEvent}
          marketingOptInChecked={marketingOptInChecked}
        />
        <Box mt={4} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleClick}
            disabled={!success}
          >
            {submitButtonText}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default ReviewAndPay
